import React, { useState, useCallback } from "react";
import { 
    Modal, 
    Box, 
    Typography, 
    IconButton, 
    Button, 
    Fade, 
    Backdrop, 
    Alert,
    LinearProgress,
    Chip,
    Tooltip
} from "@mui/material";
import { 
    Close as CloseIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Print as PrintIcon,
    Fullscreen as FullscreenIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon
} from "@mui/icons-material";
import { Worker, Viewer, Plugin, ProgressBar } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { printPlugin } from "@react-pdf-viewer/print";
import { fullScreenPlugin } from "@react-pdf-viewer/full-screen";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "@react-pdf-viewer/print/lib/styles/index.css";
import "@react-pdf-viewer/full-screen/lib/styles/index.css";

const MLSAgreementDetailModal = ({ visible, onClose, pdfUrl }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [documentInfo, setDocumentInfo] = useState(null);

    // PDF.js plugins for enhanced functionality
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) => [
            defaultTabs[0], // Thumbnails
            defaultTabs[1], // Outline
        ],
    });
    
    const zoomPluginInstance = zoomPlugin();
    const printPluginInstance = printPlugin();
    const fullScreenPluginInstance = fullScreenPlugin();
    
    const { ZoomIn, ZoomOut, Zoom } = zoomPluginInstance;
    const { Print } = printPluginInstance;
    const { EnterFullScreen } = fullScreenPluginInstance;

    const handleDocumentLoad = useCallback((e) => {
        setIsLoading(false);
        setDocumentInfo({
            numberOfPages: e.doc.numPages,
            title: e.doc._pdfInfo?.title || 'Northstar MLS End User License Agreement'
        });
    }, []);

    const handleDocumentLoadError = useCallback((error) => {
        setIsLoading(false);
        setLoadError('Failed to load the PDF document. Please try again.');
        console.error('PDF load error:', error);
    }, []);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'Northstar_MLS_License_Agreement.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Modal 
            open={visible} 
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 1, sm: 2 }
            }}
        >
            <Fade in={visible} timeout={500}>
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        width: { xs: '98%', sm: '95%', md: '90%', lg: '85%' },
                        height: { xs: '95vh', sm: '90vh' },
                        maxWidth: '1200px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                    }}
                >
                    {/* Enhanced Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            color: 'white',
                            flexShrink: 0
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <DescriptionIcon sx={{ fontSize: 32 }} />
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
                                    License Agreement
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Northstar MLS End User License Agreement
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Header Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {documentInfo && (
                                <Chip 
                                    label={`${documentInfo.numberOfPages} pages`}
                                    size="small"
                                    sx={{ 
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                            )}
                            
                            <Tooltip title="Download PDF">
                                <IconButton 
                                    onClick={handleDownload}
                                    sx={{ 
                                        color: 'white',
                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                                    }}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                            
                            <IconButton 
                                onClick={onClose}
                                sx={{ 
                                    color: 'white',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { 
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                                aria-label="Close modal"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Toolbar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            flexShrink: 0
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                            Use the toolbar below to navigate, zoom, and interact with the document
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <ZoomOut>
                                {(props) => (
                                    <Tooltip title="Zoom Out">
                                        <IconButton {...props} size="small">
                                            <ZoomOutIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ZoomOut>
                            
                            <ZoomIn>
                                {(props) => (
                                    <Tooltip title="Zoom In">
                                        <IconButton {...props} size="small">
                                            <ZoomInIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ZoomIn>
                            
                            <Print>
                                {(props) => (
                                    <Tooltip title="Print">
                                        <IconButton {...props} size="small">
                                            <PrintIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Print>
                            
                            <EnterFullScreen>
                                {(props) => (
                                    <Tooltip title="Fullscreen">
                                        <IconButton {...props} size="small">
                                            <FullscreenIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </EnterFullScreen>
                        </Box>
                    </Box>

                    {/* Loading State */}
                    {isLoading && (
                        <Box sx={{ p: 3 }}>
                            <LinearProgress sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary" align="center">
                                Loading PDF document...
                            </Typography>
                        </Box>
                    )}

                    {/* Error State */}
                    {loadError && (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {loadError}
                            </Alert>
                            <Button 
                                variant="contained" 
                                onClick={() => {
                                    setLoadError(null);
                                    setIsLoading(true);
                                }}
                                sx={{ display: 'block', mx: 'auto' }}
                            >
                                Try Again
                            </Button>
                        </Box>
                    )}

                    {/* PDF Viewer */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            bgcolor: '#f5f5f5',
                            '& .rpv-core__viewer': {
                                height: '100%'
                            },
                            '& .rpv-default-layout__container': {
                                height: '100%'
                            }
                        }}
                    >
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.10.377/build/pdf.worker.min.js">
                            <Viewer
                                fileUrl={pdfUrl}
                                plugins={[
                                    defaultLayoutPluginInstance,
                                    zoomPluginInstance,
                                    printPluginInstance,
                                    fullScreenPluginInstance
                                ]}
                                onDocumentLoad={handleDocumentLoad}
                                onDocumentLoadError={handleDocumentLoadError}
                                renderLoader={(percentages) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            flexDirection: 'column',
                                            gap: 2
                                        }}
                                    >
                                        <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                        <Typography variant="h6">Loading Document...</Typography>
                                        <Box sx={{ width: 200 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={Math.round(percentages * 100)} 
                                                sx={{ height: 8, borderRadius: 4 }}
                                            />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {Math.round(percentages * 100)}%
                                        </Typography>
                                    </Box>
                                )}
                            />
                        </Worker>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Northstar MLS End User License Agreement
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={onClose}
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default MLSAgreementDetailModal;
